import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { BRAND_COLORS } from "../lib/bookingStatus";

/** Renders an image, audio player, or download link depending on the file's mime type. */
export function ChatAttachment({ fileId, mine, transcription, messageId, editable, onTranscriptionUpdate }) {
  const [src, setSrc] = useState(null);
  const [type, setType] = useState(null);
  const [filename, setFilename] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(transcription || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft(transcription || ""); }, [transcription]);
  useEffect(() => {
    let url;
    (async () => {
      try {
        const res = await api.get(`/files/${fileId}`, { responseType: "blob" });
        url = URL.createObjectURL(res.data);
        setSrc(url);
        setType(res.data.type || res.headers?.["content-type"] || "");
        const cd = res.headers?.["content-disposition"] || "";
        const m = cd.match(/filename="?([^";]+)"?/);
        if (m) setFilename(m[1]);
      } catch (e) { /* ignore */ }
    })();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [fileId]);

  const saveTranscription = async () => {
    if (!draft.trim()) return toast.error("Transkription darf nicht leer sein.");
    setSaving(true);
    try {
      await api.patch(`/messages/${messageId}/transcription`, { voice_transcription: draft.trim() });
      onTranscriptionUpdate?.(draft.trim());
      toast.success("Transkription gespeichert.");
      setEditing(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen."); }
    finally { setSaving(false); }
  };

  if (!src) return <div className="w-40 h-10 bg-navy/5 animate-pulse" />;
  if (type && type.startsWith("image/")) {
    return <img src={src} alt="attachment" className="max-w-[280px] max-h-[280px] object-cover border border-navy/10" />;
  }
  if (type && type.startsWith("audio/")) {
    return (
      <div className="w-full max-w-[280px]" data-testid={`voice-msg-${fileId}`}>
        <div className="label-caps mb-1 flex items-center gap-1.5" style={{ color: BRAND_COLORS.gold }}>
          🎙 Sprachnachricht
        </div>
        <audio controls src={src} className="w-full max-w-[280px]" style={{ height: 40 }} />
        {editing ? (
          <div className="mt-2" style={{ maxWidth: 300 }}>
            <textarea
              data-testid={`voice-edit-textarea-${fileId}`}
              rows={3}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className={`w-full text-[13px] px-2 py-1 border ${mine ? "bg-white text-navy border-gold" : "bg-white text-navy border-navy/30"} focus:outline-none focus:border-gold`}
            />
            <div className="mt-1 flex gap-2">
              <button data-testid={`voice-save-btn-${fileId}`} onClick={saveTranscription} disabled={saving}
                className="text-[10.5px] tracking-widest uppercase px-3 py-1 hover:opacity-80 disabled:opacity-50"
                style={{ background: BRAND_COLORS.navy, color: BRAND_COLORS.ivory }}>{saving ? "…" : "Speichern"}</button>
              <button onClick={() => { setDraft(transcription || ""); setEditing(false); }}
                className={`text-[10.5px] tracking-widest uppercase px-3 py-1 ${mine ? "text-ivory/70" : "text-navy-dark/60"} hover:text-navy`}>Abbrechen</button>
            </div>
          </div>
        ) : transcription ? (
          <div className={`mt-2 flex items-start gap-2`} style={{ maxWidth: 300 }}>
            <div data-testid={`voice-transcript-${fileId}`}
              className={`text-[13px] italic leading-relaxed flex-1 ${mine ? "text-ivory/85" : "text-navy-dark/85"}`}>
              „{transcription}"
            </div>
            {editable && messageId && (
              <button data-testid={`voice-edit-btn-${fileId}`} onClick={() => setEditing(true)}
                title="Transkription korrigieren"
                className={`text-[13px] shrink-0 leading-none ${mine ? "text-ivory/70 hover:text-ivory" : "text-navy-dark/50 hover:text-navy"}`}>
                ✏️
              </button>
            )}
          </div>
        ) : editable && messageId ? (
          <button onClick={() => setEditing(true)}
            className={`mt-2 text-[11px] tracking-widest uppercase border-b ${mine ? "text-ivory/60 border-gold/40" : "text-navy-dark/60 border-gold"} hover:text-gold`}>
            + Transkription hinzufügen
          </button>
        ) : null}
      </div>
    );
  }
  return <a href={src} download={filename || fileId} className={`text-[12px] underline ${mine ? "text-ivory" : "text-navy"}`}>📎 {filename || "Download"}</a>;
}

/** Microphone recorder: click to start, click to stop → uploads audio and returns the FileRef via onSent. */
export function VoiceRecorder({ onSent, disabled }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [uploading, setUploading] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime });
        setUploading(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, `voice-${Date.now()}.webm`);
          const { data: fileRef } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          const { data: msg } = await api.post("/messages", { attachment_id: fileRef.id });
          onSent?.(msg);
        } catch (_) { /* toast handled upstream */ }
        finally { setUploading(false); }
      };
      recRef.current = rec;
      rec.start();
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (e) {
      alert("Mikrofonzugriff verweigert. Bitte in den Browsereinstellungen erlauben.");
    }
  };

  const stop = () => {
    if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
    if (timerRef.current) clearInterval(timerRef.current);
    setRecording(false);
  };

  const cancel = () => {
    if (recRef.current && recRef.current.state !== "inactive") {
      chunksRef.current = []; // discard so onstop uploads nothing
      recRef.current.onstop = () => recRef.current.stream?.getTracks().forEach((t) => t.stop());
      recRef.current.stop();
    }
    if (timerRef.current) clearInterval(timerRef.current);
    setRecording(false);
  };

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const mmss = `${String(Math.floor(seconds / 60)).padStart(1, "0")}:${String(seconds % 60).padStart(2, "0")}`;

  if (recording) {
    return (
      <div className="flex items-center gap-2" data-testid="voice-recording">
        <button type="button" onClick={cancel} title="Abbrechen"
          className="border border-navy/30 text-navy-dark/70 hover:text-navy px-2 py-2.5 text-[12px]">
          ✕
        </button>
        <div className="flex items-center gap-2 px-3 py-2.5 border border-red-300 bg-red-50/50">
          <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" />
          <span className="text-[13px] text-red-700 font-mono tabular-nums">{mmss}</span>
        </div>
        <button type="button" data-testid="voice-stop-btn" onClick={stop} disabled={uploading}
          className="text-ivory px-4 py-2.5 text-[11px] tracking-widest uppercase hover:opacity-90 disabled:opacity-50"
          style={{ background: BRAND_COLORS.gold, color: BRAND_COLORS.navy }}>
          {uploading ? "…" : "Senden ▶"}
        </button>
      </div>
    );
  }
  return (
    <button type="button" data-testid="voice-record-btn" onClick={start} disabled={disabled || uploading}
      title="Sprachnachricht aufnehmen"
      className="border border-navy/20 text-navy px-3 py-2.5 text-[16px] hover:border-gold hover:text-gold transition-colors disabled:opacity-50">
      🎙
    </button>
  );
}
